<script lang="ts" module>
	type Tag = "h2" | "h1" | "h3" | "h4" | "h5" | "h6" | undefined;
</script>

<script lang="ts">
	import { Heading } from "flowbite-svelte-v1";
	import DOMPurify from "isomorphic-dompurify";
	import { getContext } from "svelte";
	import { twMerge } from "tailwind-merge";
	import { Mode } from "#gen/bundles/v1/service_pb.js";
	import TokenizedText from "#lib/components/TokenizedText.svelte";
	import { DEFAULT_HEADER_COMPONENT_TEXT } from "#lib/components/flow/editor/appearance/constants.js";
	import type { ComponentProps } from "#lib/components/page/ComponentProps.js";
	import { stringValue, structValue } from "#lib/struct.js";
	import type { TokenProvider } from "#lib/tokenProvider.js";
	import type { Writable } from "svelte/store";

	let { id, style, page, mode }: ComponentProps = $props();

	const config = $derived(structValue($page.config, id));
	const tag = $derived((stringValue(config, "tag") as Tag) ?? "h6");
	const align = $derived(stringValue(config, "align") ?? "left");
	const tokenProvider: Writable<TokenProvider> = getContext("tokenProvider");

	// The base look comes from the .heading-consumer control defaults. The
	// build-your-own-page overrides below are layered on only when the page
	// explicitly sets them, so an un-customized heading is not forced onto the h2
	// appearance defaults.
	const textColor = $derived(stringValue(config, "textColor"));
	const textSize = $derived(stringValue(config, "textSize"));
	const textAlignment = $derived(stringValue(config, "textAlignment"));
	const fontWeight = $derived(stringValue(config, "fontWeight"));

	const textAppearanceStyle = $derived(() => {
		// .heading-consumer pins the base color with !important, so an explicitly
		// configured color must also be !important to override it. The other
		// properties are layered without !important and win on their own.
		const color = textColor || stringValue($page.themeOverrides, "headerTextColor");
		const styleMap: Record<string, string | undefined> = {
			color: color ? `${color} !important` : undefined,
			"text-align": textAlignment,
			"font-weight": fontWeight,
			"font-size": textSize ? `var(--text-${textSize})` : undefined,
		};

		return Object.entries(styleMap)
			.filter(([, v]) => v)
			.map(([k, v]) => `${k}: ${v};`)
			.join(" ");
	});

	const resolvedStyle = $derived(() => [style, textAppearanceStyle()].filter(Boolean).join("; "));
	const configText = $derived(stringValue(config, "text"));
	const isEditing = $derived(mode && mode === Mode.EDITING);
	const resolvedFallback = $derived(isEditing ? DEFAULT_HEADER_COMPONENT_TEXT : "");
	const showTokenizedPreview = $derived(isEditing && $tokenProvider?.hasSubstitutionToken(configText));
	const resolvedEditorDisplayText = $derived(configText !== undefined && configText !== "" ? configText : resolvedFallback);
</script>

<Heading {tag} class={twMerge("heading-consumer", `text-${align}`, "wrap-break-word", stringValue(config, "class") ?? "")} style={resolvedStyle()}>
	{#if showTokenizedPreview}
		<TokenizedText provider={$tokenProvider} text={configText} {mode} />
	{:else if isEditing}
		{resolvedEditorDisplayText}
	{:else}
		<!-- eslint-disable-next-line svelte/no-at-html-tags -->
		{@html DOMPurify.sanitize(resolvedEditorDisplayText) ?? ""}
	{/if}
</Heading>
